/**
 * POLineJobStatusTab — Amazon-tracking-style job progress (modal-only).
 *
 * Three layers, by importance:
 *   1. Line milestones (Order received → Contract reviewed) — a single
 *      compact strip of two muted check-marks. They happen once per
 *      line and aren't load-bearing once the jobs are running.
 *   2. Aggregate progress bar — stacked [dispatched | in-process |
 *      remaining] with totals. This is the "what's the state of the
 *      whole order" answer the user actually asks first.
 *   3. Per-sub-job rows — one row per (job_root, suffix) with a
 *      compact horizontal stepper. Rows scale: lines have anywhere
 *      from 1 to 13 sub-jobs in real data, so we use a dense row
 *      layout, not full-width cards per job.
 *
 * Data shape from real DB sweep (425k events / 109k lines):
 *   - 95% of jobs have no split (1 sub-job).
 *   - 5% split into 2-13 sub-jobs (parent + suffixes _1, _2, ...).
 *   - Each sub-job has its own qty (essentially independent).
 *   - po_line.quantity = sum of sub-job qtys (approx; ERP sometimes
 *     restarts a sub-job which double-counts in the changelog).
 */
import { useMemo, useState, useEffect } from 'react';
import {
  Check,
  Inbox,
  FileCheck2,
  Clock,
  PauseCircle,
  type LucideIcon,
} from 'lucide-react';
import type { ChangelogEntry } from '../common/Changelog';
import type { PurchaseOrderLine, Job } from '../../lib/api';
import { purchasingAPI } from '../../lib/api';
import JobSubJobRow from './JobSubJobRow';
import type { JobTrack } from './jobTrack';
import { useFormatters } from '../../contexts/PreferencesContext';
import { parseProgress, relativeAge } from './jobStatusParsing';

interface POLineJobStatusTabProps {
  changelogs: ChangelogEntry[];
  line: PurchaseOrderLine | null;
}

export default function POLineJobStatusTab({ changelogs, line }: POLineJobStatusTabProps) {
  const { formatDate } = useFormatters();

  // Real job records from the ERP. Preferred over the change-log derivation
  // below, which stays only for lines whose jobs have not been imported yet.
  const [erpJobs, setErpJobs] = useState<Job[] | null>(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  useEffect(() => {
    if (!line?.id) return;
    let cancelled = false;
    setJobsLoading(true);
    purchasingAPI.getPOLineJobs(line.id)
      .then((res) => { if (!cancelled) setErpJobs(res.data.results); })
      .catch(() => { if (!cancelled) setErpJobs(null); })
      .finally(() => { if (!cancelled) setJobsLoading(false); });
    return () => { cancelled = true; };
  }, [line?.id]);

  const { lineOrderAt, lineContractAt, jobs, hasAny, dispatchedQty, inProcessQty } = useMemo(() => {
    // Preferred path: real ERP job records. Statuses are read, not inferred
    // from wording, and split sub-jobs come through as their own rows rather
    // than being recovered from a "_1" suffix on the number.
    if (erpJobs && erpJobs.length > 0) {
      const tracks: JobTrack[] = erpJobs.map((j) => {
        // Read the backend's own verdict rather than comparing status codes:
        // the ERP scale is not ordinal (3 is terminal, 4 never occurs).
        const dispatched = j.is_dispatched;
        const inProcess = j.is_dispatched || j.is_in_process;
        const latest = j.completed_at || j.started_at || j.updated_at;
        return {
          jobNumber: j.job_number,
          qty: j.quantity,
          inProcess,
          dispatched,
          inProcessAt: inProcess ? (j.started_at ?? null) : null,
          dispatchedAt: dispatched ? (j.completed_at ?? null) : null,
          latestAt: latest,
          latestStatus: j.on_hold ? 'on hold' : j.status_label,
          promiseAt: j.due_date ?? null,
          events: j.events ?? [],
          record: j,
        };
      });

      let dispatchedTotal = 0;
      let inProcessTotal = 0;
      for (const j of erpJobs) {
        if (j.is_dispatched) dispatchedTotal += j.quantity;
        else if (j.is_in_process) inProcessTotal += j.quantity;
      }

      // Line-level milestones still only exist in the change log.
      const milestones = changelogs.filter((e) => e.field_changed === 'progress');
      let orderReceived: string | null = null;
      let contractReviewed: string | null = null;
      for (const e of milestones) {
        const text = (e.new_value || '').toLowerCase();
        if (text.includes('order') && text.includes('received') && !orderReceived) {
          orderReceived = e.change_date;
        } else if (text.includes('contract') && !contractReviewed) {
          contractReviewed = e.change_date;
        }
      }

      return {
        lineOrderAt: orderReceived,
        lineContractAt: contractReviewed,
        jobs: tracks,
        hasAny: true,
        dispatchedQty: dispatchedTotal,
        inProcessQty: inProcessTotal,
      };
    }

    // Fallback: reconstruct from change-log prose. Only reached for lines
    // whose jobs the ERP has not yet sent as records.
    const progress = changelogs
      .filter(e => e.field_changed === 'progress')
      .sort((a, b) => new Date(a.change_date).getTime() - new Date(b.change_date).getTime());

    let orderAt: string | null = null;
    let contractAt: string | null = null;
    const map = new Map<string, JobTrack>();

    for (const e of progress) {
      const p = parseProgress(e.new_value);
      const text = p.statusText.toLowerCase();
      if (!p.jobNumber) {
        if (text.includes('order') && text.includes('received') && !orderAt) orderAt = e.change_date;
        else if (text.includes('contract') && (text.includes('complete') || text.includes('review')) && !contractAt) contractAt = e.change_date;
        continue;
      }
      const qty = p.qty ? parseInt(p.qty, 10) : 0;
      const existing = map.get(p.jobNumber) ?? {
        jobNumber: p.jobNumber,
        qty,
        inProcess: false,
        dispatched: false,
        inProcessAt: null,
        dispatchedAt: null,
        latestAt: e.change_date,
        latestStatus: p.statusText,
        promiseAt: null,   // the change-log never carried a per-sub-job date
        events: [],
        record: null,      // no ERP record on this path — Details is hidden
      };
      const isDispatchEvent = /dispatch|despatch|shipped|complete|packed/i.test(text);
      const isProcessEvent = /process|progress|inspect/i.test(text);
      if (isDispatchEvent && !existing.dispatchedAt) {
        existing.dispatchedAt = e.change_date;
        existing.dispatched = true;
      }
      if (isProcessEvent && !existing.inProcessAt) {
        existing.inProcessAt = e.change_date;
        existing.inProcess = true;
      }
      existing.qty = qty || existing.qty;
      existing.latestAt = e.change_date;
      existing.latestStatus = p.statusText;
      map.set(p.jobNumber, existing);
    }

    const jobsOut = Array.from(map.values()).sort((a, b) => {
      const aStart = a.inProcessAt ?? a.latestAt;
      const bStart = b.inProcessAt ?? b.latestAt;
      return new Date(aStart).getTime() - new Date(bStart).getTime();
    });

    let dispatched = 0;
    let inProcess = 0;
    for (const j of jobsOut) {
      if (j.dispatched) dispatched += j.qty;
      else if (j.inProcess) inProcess += j.qty;
    }

    return {
      lineOrderAt: orderAt,
      lineContractAt: contractAt,
      jobs: jobsOut,
      hasAny: progress.length > 0,
      dispatchedQty: dispatched,
      inProcessQty: inProcess,
    };
  }, [changelogs, erpJobs]);

  if (jobsLoading && !hasAny) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        Loading job status…
      </div>
    );
  }

  if (!hasAny && !line?.on_hold) {
    return (
      <div className="text-center py-12">
        <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No progress updates yet.</p>
        <p className="text-xs text-muted-foreground mt-1">
          Status will appear here once the customer&rsquo;s ERP processes this line.
        </p>
      </div>
    );
  }

  // Hold state lives on po_line, not in the progress changelog (separate
  // field_changed='on_hold' channel). Banner sits above everything else
  // because hold blocks all other progress and is the answer to "why
  // hasn't anything moved?".
  const holdBanner = line?.on_hold ? (
    <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <PauseCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-amber-900">
          On hold
          {line.on_hold_date && (
            <span className="font-normal text-amber-700"> · since {formatDate(line.on_hold_date)} ({relativeAge(line.on_hold_date)})</span>
          )}
          {line.on_hold_by_company_type && (
            <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
              by {line.on_hold_by_company_type}
            </span>
          )}
        </div>
        {line.on_hold_reason_text && (
          <div className="text-xs text-amber-800 mt-1">{line.on_hold_reason_text}</div>
        )}
      </div>
    </div>
  ) : null;

  // Aggregate qty math:
  //   ordered = po_line.quantity (authoritative, from PO)
  //   dispatched = sum of qty across sub-jobs whose latest event is "dispatched"
  //   in-process = sum of qty across sub-jobs whose latest event is "in process"
  //   remaining = ordered - dispatched - in-process (clamped at 0; ERP restarts can over-count)
  const ordered = line ? Math.round(parseFloat(line.quantity)) : (dispatchedQty + inProcessQty);
  const dispatchedClamped = Math.min(dispatchedQty, ordered);
  const inProcessClamped = Math.min(inProcessQty, Math.max(0, ordered - dispatchedClamped));
  const remaining = Math.max(0, ordered - dispatchedClamped - inProcessClamped);
  const total = Math.max(1, ordered);
  const dispatchedPct = (dispatchedClamped / total) * 100;
  const inProcessPct = (inProcessClamped / total) * 100;
  const remainingPct = (remaining / total) * 100;

  return (
    <div className="space-y-5">
      {holdBanner}

      {/* Layer 1 — line milestones as a compact header strip (not a card) */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground border-b pb-3">
        <Milestone done={!!lineOrderAt} icon={Inbox} label="Order received" when={lineOrderAt ? formatDate(lineOrderAt) : null} formatDate={formatDate} />
        <span aria-hidden="true">→</span>
        <Milestone done={!!lineContractAt} icon={FileCheck2} label="Contract reviewed" when={lineContractAt ? formatDate(lineContractAt) : null} formatDate={formatDate} />
      </div>

      {/* Layer 2 — aggregate qty progress: compact, inline with summary text.
          Max-width keeps the bar from sprawling across the modal on simple
          single-sub-job lines where it would otherwise dominate. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="text-sm">
          <span className="font-semibold">{dispatchedClamped + inProcessClamped}</span>
          <span className="text-muted-foreground"> of {ordered} in production</span>
        </div>
        <div className="flex h-1.5 w-48 rounded-full overflow-hidden bg-slate-100" aria-label="Quantity progress">
          {dispatchedClamped > 0 && (
            <div className="bg-emerald-500" style={{ width: `${dispatchedPct}%` }} title={`${dispatchedClamped} dispatched`} />
          )}
          {inProcessClamped > 0 && (
            <div className="bg-sky-400" style={{ width: `${inProcessPct}%` }} title={`${inProcessClamped} in process`} />
          )}
          {remainingPct > 0 && (
            <div className="bg-slate-200" style={{ width: `${remainingPct}%` }} title={`${remaining} not yet started`} />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 text-xs">
          <Legend swatch="bg-emerald-500" label="Dispatched" qty={dispatchedClamped} />
          <Legend swatch="bg-sky-400" label="In process" qty={inProcessClamped} />
          {remaining > 0 && <Legend swatch="bg-slate-200" label="Not started" qty={remaining} />}
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          {jobs.length} {jobs.length === 1 ? 'sub-job' : 'sub-jobs'}
        </div>
      </div>

      {/* Layer 3 — one row per sub-job. Compact so 13-row lines still fit. */}
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-4 px-4 py-2 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-b">
          <div>Sub-job</div>
          <div className="text-right">Qty</div>
          <div className="text-right">Promise date</div>
          <div className="text-center">Progress</div>
          <div className="text-right">Last update</div>
        </div>
        {jobs.map(job => (
          <JobSubJobRow key={job.jobNumber} track={job} />
        ))}
      </div>
    </div>
  );
}

function Milestone({ done, icon: Icon, label, when }: {
  done: boolean;
  icon: LucideIcon;
  label: string;
  when: string | null;
  formatDate: (d: string) => string;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${done ? 'text-foreground' : 'text-muted-foreground/60'}`}>
      <span className={`relative h-4 w-4 rounded-full flex items-center justify-center ${done ? 'bg-emerald-500' : 'bg-slate-200'}`}>
        {done ? (
          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} aria-hidden="true" />
        ) : (
          <Icon className="h-2.5 w-2.5 text-slate-400" aria-hidden="true" />
        )}
      </span>
      <span className="font-medium">{label}</span>
      {when && <span className="text-muted-foreground tabular-nums">{when}</span>}
    </span>
  );
}

function Legend({ swatch, label, qty }: { swatch: string; label: string; qty: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span className={`h-2.5 w-2.5 rounded-sm ${swatch}`} aria-hidden="true" />
      <span>{label}</span>
      <span className="font-mono text-foreground">{qty}</span>
    </span>
  );
}
